// errorConfig.ts
import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { history } from '@umijs/max';

/** 后端统一响应格式 */
interface ResponseStructure {
  code: number;
  message?: string; // 后端可能返回空
  data: any;
  timestamp?: number;
  success?: boolean;
}

/** 业务错误码 */
enum BusinessErrorCode {
  SUCCESS = 200,
  CREATED = 201,
  ACCEPTED = 202,
  NO_CONTENT = 204,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  TOKEN_EXPIRED = 4011,
  TOKEN_INVALID = 4012,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  VALIDATION_FAILED = 422,
  INTERNAL_SERVER_ERROR = 500,
}

/** 本地备用消息 */
const businessErrorMessages: Record<number, string> = {
  [BusinessErrorCode.BAD_REQUEST]: '请求参数错误，请检查后重试。',
  [BusinessErrorCode.UNAUTHORIZED]: '用户名或密码错误。',
  [BusinessErrorCode.TOKEN_EXPIRED]: '登录状态已过期，请重新登录。',
  [BusinessErrorCode.TOKEN_INVALID]: '登录状态无效，请重新登录。',
  [BusinessErrorCode.FORBIDDEN]: '没有权限访问该资源。',
  [BusinessErrorCode.NOT_FOUND]: '请求的资源不存在。',
  [BusinessErrorCode.VALIDATION_FAILED]: '业务校验失败。',
  [BusinessErrorCode.INTERNAL_SERVER_ERROR]: '服务器内部错误，请稍后重试。',
};

const httpStatusMessages: Record<number, string> = {
  400: '请求参数错误，请检查后重试。',
  401: '未登录或登录已过期，请重新登录。',
  403: '没有权限访问该资源。',
  404: '请求的资源不存在。',
  500: '服务器内部错误，请稍后重试。',
};

/** 判断是否成功响应（业务 code） */
const isSuccessResponse = (code: number) => code >= 200 && code < 300;

/** 获取友好提示：优先使用后端返回的 message，否则使用本地或默认消息 */
const getFriendlyErrorMessage = (code: number, backendMessage?: string) => {
  if (backendMessage && backendMessage.trim() !== '') return backendMessage;
  return businessErrorMessages[code] || httpStatusMessages[code] || '请求失败，请稍后重试。';
};

/** 错误展示类型 */
const getErrorShowType = (code: number) => {
  if ([BusinessErrorCode.UNAUTHORIZED, BusinessErrorCode.TOKEN_EXPIRED, BusinessErrorCode.TOKEN_INVALID].includes(code)) {
    return 'NOTIFICATION';
  }
  if (code === BusinessErrorCode.FORBIDDEN) return 'WARN_MESSAGE';
  if (code >= 500) return 'ERROR_MESSAGE';
  return 'ERROR_MESSAGE';
};

/** 处理认证错误 */
const handleAuthError = (code: number, messageStr: string, url?: string) => {
  const isLoginUrl = Boolean(url?.includes('/login') || url?.includes('/login/account'));

  if ([BusinessErrorCode.TOKEN_EXPIRED, BusinessErrorCode.TOKEN_INVALID].includes(code) && !isLoginUrl) {
    notification.warning({ message: '登录状态已失效', description: '请重新登录', duration: 2 });
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    setTimeout(() => history.push('/user/login'), 1200);
    return;
  }

  if (code === BusinessErrorCode.UNAUTHORIZED && isLoginUrl) {
    message.error('用户名或密码错误');
    return;
  }

  if (code === BusinessErrorCode.UNAUTHORIZED && !isLoginUrl) {
    notification.error({ message: '未授权访问', description: messageStr });
    localStorage.removeItem('token');
    setTimeout(() => history.push('/user/login'), 1200);
    return;
  }
};

export const errorConfig: RequestConfig = {
  errorConfig: {
    /** 把后端非成功响应转换成异常 */
    errorThrower: (res: ResponseStructure) => {
      if (!res || typeof res.code !== 'number') return;

      if (!isSuccessResponse(res.code)) {
        const error: any = new Error(res.message || '业务错误');
        error.name = 'BizError';
        error.info = {
          code: res.code,
          message: res.message, // 后端消息
          data: res.data,
          showType: getErrorShowType(res.code),
        };
        if ((res as any)._config) error.config = (res as any)._config;
        throw error;
      }
    },

    /** 全局 errorHandler */
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 业务错误
      if (error?.name === 'BizError') {
        const { code, message: backendMsg, showType } = error.info || {};
        const url = error.config?.url || error.response?.config?.url;
        const friendlyMessage = getFriendlyErrorMessage(code, backendMsg);

        if ([BusinessErrorCode.UNAUTHORIZED, BusinessErrorCode.TOKEN_EXPIRED, BusinessErrorCode.TOKEN_INVALID].includes(code)) {
          handleAuthError(code, friendlyMessage, url);
          return;
        }

        switch (showType) {
          case 'SILENT':
            break;
          case 'WARN_MESSAGE':
            message.warning(friendlyMessage);
            break;
          case 'NOTIFICATION':
            notification.error({ message: `错误代码: ${code}`, description: friendlyMessage });
            break;
          default:
            message.error(friendlyMessage);
        }
        return;
      }

      // HTTP 网络错误
      if (error?.response) {
        const status = error.response.status;
        const backendMsg = error.response?.data?.message;
        const friendlyMessage = getFriendlyErrorMessage(status, backendMsg);

        if (status === 401) {
          handleAuthError(BusinessErrorCode.UNAUTHORIZED, friendlyMessage, error.config?.url || error.response?.config?.url);
        } else {
          message.error(friendlyMessage);
        }

        if (process.env.NODE_ENV === 'development') {
          console.error('HTTP Error Details:', {
            status,
            url: error.config?.url || error.response?.config?.url,
            method: error.config?.method || error.response?.config?.method,
            responseData: error.response.data,
          });
        }
        return;
      }

      // 网络异常
      if (error?.request) {
        message.error('网络连接异常，请检查网络连接后重试。');
        return;
      }

      // 其他未知错误
      message.error('请求发送失败，请重试。');
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = localStorage.getItem('token');
      if (token) config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
      const url = config.url || '';
      config.url = url.includes('?') ? `${url}&_t=${Date.now()}` : `${url}?_t=${Date.now()}`;
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response: any) => {
      const data = response?.data as ResponseStructure | undefined;
      if (data && typeof data.code === 'number' && !isSuccessResponse(data.code)) {
        if (errorConfig.errorConfig?.errorThrower) {
          (data as any)._config = response?.config; // 挂载 config
          errorConfig.errorConfig.errorThrower(data);
        }
      }
      return response;
    },
  ],
};

export default errorConfig;
